import React, { useEffect, ChangeEvent, useRef } from 'react';
import { Form } from 'react-bootstrap';
import { InputFieldState } from '../types';

/**
 * AddressFormコンポーネント用のPropsです。
 * @interface AddressFormProps
 */
interface AddressFormProps {
  /**
   * 住所又は居所
   * @type {InputFieldState}
   * @memberof AddressFormProps
   */
  address: InputFieldState;

  /**
   * 代理人フラグ
   * @type {boolean}
   * @memberof AddressFormProps
   */
  isAgency: boolean;

  /**
   * 住所又は居所を設定する関数
   * @memberof AddressFormProps
   */
  onChange: (address: InputFieldState) => void;
}

/**
 * 住所又は居所フォームのコンポーネントです。
 * @param {InputFieldState} props.address - 表示する住所又は居所
 * @param {boolean} props.isAgency - 代理人フラグ
 * @param {function} props.onChange - 住所又は居所が変更されたときに呼ばれるコールバック関数
 * @returns {JSX.Element} - 住所又は居所フォームのJSX要素
 */
const AddressForm: React.FC<AddressFormProps> = ({ address, isAgency, onChange }) => {

  const renderFlgRef = useRef(false);

  useEffect(() => {

    if(renderFlgRef.current) {
      // ２回目以降であればチェック対象とする

      // 在外者は利用できいないツールのため、簡易的に住所チェックをおこなう
      const pattern1 = /^(北海道|青森県|岩手県|宮城県|秋田県|山形県|福島県|茨城県|栃木県|群馬県|埼玉県|千葉県|東京都|神奈川県|新潟県|富山県|石川県|福井県|山梨県|長野県|岐阜県|静岡県|愛知県|三重県|滋賀県|京都府|大阪府|兵庫県|奈良県|和歌山県|鳥取県|島根県|岡山県|広島県|山口県|徳島県|香川県|愛媛県|高知県|福岡県|佐賀県|長崎県|熊本県|大分県|宮崎県|鹿児島県|沖縄県)/;
      const pattern2 = /(...??[都道府県])((?:旭川|伊達|石狩|盛岡|奥州|田村|南相馬|那須塩原|東村山|武蔵村山|羽村|十日町|上越|富山|野々市|大町|蒲郡|四日市|姫路|大和郡山|廿日市|下松|岩国|田川|大村)市|.+?郡(?:玉村|大町|.+?)[町村]|.+?市.+?区|.+?[市区町村])(.+)/;

      let errorMessage = '';
      if(!isAgency && address.value === '') { 
        // 提出者（委任者）かつ住所が空の場合
        errorMessage = '住所又は居所が入力されていません。';
      } else if(address.value !== '' && !address.value.match(pattern1)) {
        // 住所が都道府県から始まっていない場合
        errorMessage = '住所又は居所が都道府県から記載されていません。';
      } else if(address.value !== '' && !address.value.match(pattern2)) {
        // 住所が「〇〇（都道府県）〇〇（市区町村）〇〇」表記で記載されていない場合
        errorMessage = '住所又は居所が「〇〇（都道府県）〇〇（市区町村）〇〇」の表記で記載されていません。';
      } else {
        errorMessage = '';
      }
      onChange({
        value: address.value,
        errorMessage: errorMessage
      });
      
    } else {
      // 初回は実行しない
      renderFlgRef.current = true
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [address.value]);

  /**
   * フォームの変更イベントハンドラーです。
   * 入力された住所又は居所をonChange関数を呼び出します。
   * @param {object} event - フォームの変更イベント
   * @returns {void}
   */
  const handleChange = (event: ChangeEvent<HTMLInputElement>): void => {
    const value = halfWidthToFullWidth(event.target.value);
    onChange({
      value: value,
      errorMessage: address.errorMessage
    });
  };

  /**
   * 半角文字を全角文字に変換する
   * @param {String} inputValue 変換対象の半角文字列
   * @return {String} 全角変換した文字列
   */
  const halfWidthToFullWidth = (inputValue: string) => {
    const fullWidthValue = inputValue.replace(/[A-Za-z0-9]/g, (s) => {
      return String.fromCharCode(s.charCodeAt(0) + 0xFEE0);
    });

    return fullWidthValue
      .replace(/"/g, "”")
      .replace(/'/g, "’")
      .replace(/`/g, "‘")
      .replace(/\\/g, "￥")
      .replace(/ /g, "　")
      .replace(/~/g, "〜")
      .replace(/-/g, "−");
  };

  return (
    <Form.Group className="mb-2 row">
      <Form.Label className="col-lg-2 col-12 d-flex align-items-center">
        住所又は居所
        {isAgency ? <span className="text-small text-secondary ms-3">※任意</span> : <span className="text-small text-danger ms-3">※必須</span>}
      </Form.Label>
      <div className="col-lg-10 col-12">
        <Form.Control
          type="text"
          value={address.value}
          placeholder="住所又は居所を入力してください。"
          onChange={handleChange}
          aria-label="住所又は居所"
          isInvalid={address.errorMessage !== ''}
        />
        {address.errorMessage !== '' ? <div className="text-end text-danger small">{address.errorMessage}</div> : ''}
      </div>
    </Form.Group>
  );
};

export default AddressForm;